if (!customElements.get('dh-authorized-collection')) {
  customElements.define('dh-authorized-collection', class extends HTMLElement {
    connectedCallback() {
      this.handleDocumentKeydown ||= (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-filter-open')) this.closeFilters();
      };
      document.removeEventListener('keydown', this.handleDocumentKeydown);
      document.addEventListener('keydown', this.handleDocumentKeydown);
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.cards = [...this.querySelectorAll('[data-dh-collection-card]')];
      this.sortControls = [...this.querySelectorAll('[data-dh-collection-sort]')];
      this.categories = [...this.querySelectorAll('[data-dh-collection-category]')];
      this.priceMin = this.querySelector('[data-dh-collection-price-min]');
      this.priceMax = this.querySelector('[data-dh-collection-price-max]');
      this.priceSummary = this.querySelector('[data-dh-collection-price-summary]');
      this.grid = this.querySelector('[data-dh-collection-grid]');
      this.counts = [...this.querySelectorAll('[data-dh-collection-count]')];
      this.clear = this.querySelector('[data-dh-collection-clear]');
      this.toggle = this.querySelector('[data-dh-collection-filter-toggle]');
      this.highestPrice = Math.max(0, ...this.cards.map((card) => Number(card.dataset.price || 0) / 100));

      [this.priceMin, this.priceMax].forEach((input) => {
        if (!input) return;
        input.max = String(this.highestPrice);
        input.placeholder = input === this.priceMin ? '0' : String(this.highestPrice);
        input.addEventListener('input', () => this.update());
        input.addEventListener('change', () => {
          this.normalisePriceRange();
          this.update();
        });
      });

      this.toggle?.addEventListener('click', () => this.openFilters());
      this.querySelectorAll('[data-dh-collection-filter-close]').forEach((control) => {
        control.addEventListener('click', () => this.closeFilters());
      });
      this.clear?.addEventListener('click', () => this.clearFilters());
      this.sortControls.forEach((control) => {
        control.addEventListener('change', () => {
          this.sortControls.forEach((peer) => { if (peer !== control) peer.value = control.value; });
          this.update();
        });
      });
      this.categories.forEach((control) => control.addEventListener('change', () => this.update()));
      this.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.closeFilters(); });
      this.update();
    }

    disconnectedCallback() {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.handleDocumentKeydown);
    }

    openFilters() {
      this.classList.add('is-filter-open');
      document.body.style.overflow = 'hidden';
      this.toggle?.setAttribute('aria-expanded', 'true');
      this.querySelector('[data-dh-collection-filter-close]')?.focus();
    }

    closeFilters() {
      if (!this.classList.contains('is-filter-open')) return;
      this.classList.remove('is-filter-open');
      document.body.style.overflow = '';
      this.toggle?.setAttribute('aria-expanded', 'false');
      this.toggle?.focus();
    }

    clearFilters() {
      this.categories.forEach((control) => { control.checked = false; });
      if (this.priceMin) this.priceMin.value = '';
      if (this.priceMax) this.priceMax.value = '';
      this.update();
    }

    normalisePriceRange() {
      if (!this.priceMin || !this.priceMax) return;
      const minimum = this.numericValue(this.priceMin);
      const maximum = this.numericValue(this.priceMax);
      if (minimum !== null) this.priceMin.value = String(minimum);
      if (maximum !== null) this.priceMax.value = String(maximum);
      if (minimum === null || maximum === null || minimum <= maximum) return;
      this.priceMin.value = String(maximum);
      this.priceMax.value = String(minimum);
    }

    numericValue(input) {
      if (!input || input.value.trim() === '') return null;
      const value = Number(input.value);
      return Number.isFinite(value) ? Math.max(0, Math.min(value, this.highestPrice)) : null;
    }

    updatePriceSummary(minimum, maximum) {
      if (!this.priceSummary) return;
      if (minimum === null && maximum === null) this.priceSummary.textContent = 'Select';
      else if (minimum !== null && maximum !== null) this.priceSummary.textContent = `$${minimum} – $${maximum}`;
      else if (minimum !== null) this.priceSummary.textContent = `From $${minimum}`;
      else this.priceSummary.textContent = `Up to $${maximum}`;
    }

    update() {
      const selected = new Set(this.categories.filter((control) => control.checked).map((control) => control.value));
      const minimum = this.numericValue(this.priceMin);
      const maximum = this.numericValue(this.priceMax);
      this.updatePriceSummary(minimum, maximum);

      let visible = this.cards.filter((card) => {
        const price = Number(card.dataset.price || 0) / 100;
        const matchesType = !selected.size || selected.has(card.dataset.category);
        const matchesMinimum = minimum === null || price >= minimum;
        const matchesMaximum = maximum === null || price <= maximum;
        return matchesType && matchesMinimum && matchesMaximum;
      });

      const mode = this.sortControls[0]?.value || 'featured';
      visible = [...visible].sort((left, right) => {
        if (mode === 'title-ascending') return left.dataset.title.localeCompare(right.dataset.title);
        if (mode === 'title-descending') return right.dataset.title.localeCompare(left.dataset.title);
        if (mode === 'price-ascending') return Number(left.dataset.price) - Number(right.dataset.price);
        if (mode === 'price-descending') return Number(right.dataset.price) - Number(left.dataset.price);
        if (mode === 'best-selling') {
          const leftBest = left.dataset.category === 'best-sellers' ? 0 : 1;
          const rightBest = right.dataset.category === 'best-sellers' ? 0 : 1;
          return leftBest - rightBest || Number(left.dataset.featuredOrder) - Number(right.dataset.featuredOrder);
        }
        return Number(left.dataset.featuredOrder) - Number(right.dataset.featuredOrder);
      });

      this.cards.forEach((card) => { card.hidden = !visible.includes(card); });
      visible.forEach((card) => this.grid?.append(card));
      this.counts.forEach((count) => { count.textContent = String(visible.length); });
      if (this.clear) this.clear.disabled = selected.size === 0 && minimum === null && maximum === null;
    }
  });
}
